'use client'

import { motion, useInView } from 'framer-motion'
import { ChevronRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRef } from 'react'
import { Button, buttonVariants } from '@/components/ui/button'
import { siteConfig } from '@/config/site'
import { cn } from '@/utils/cn'
import { BorderBeam } from '../magicui/border-beam'
import { LandingSocialProof } from '../vemmerui/social-proof/landing-social-proof'

const fadeUpVariants = {
  initial: {
    opacity: 0,
    y: 24,
  },
  animate: {
    opacity: 1,
    y: 0,
  },
}

type AnimatedElementProps = {
  children: React.ReactNode
  inView: boolean
  delay?: number
  className?: string
}

const AnimatedElement = ({ children, inView, delay = 0.1, className }: AnimatedElementProps) => (
  <motion.div
    animate={inView ? 'animate' : 'initial'}
    variants={fadeUpVariants}
    initial={false}
    transition={{
      duration: 0.6,
      delay,
      ease: [0.21, 0.47, 0.32, 0.98],
      type: 'spring',
    }}
    className={className}
  >
    {children}
  </motion.div>
)

type HeroTitleProps = {
  inView: boolean
}

const HeroTitle = ({ inView }: HeroTitleProps) => (
  <motion.h1
    className="text-balance bg-gradient-to-br from-30% from-foreground to-foreground/60 bg-clip-text py-6 font-medium text-5xl text-transparent leading-none tracking-tighter sm:text-6xl md:text-7xl lg:text-7xl dark:from-foreground dark:to-foreground/40"
    animate={inView ? 'animate' : 'initial'}
    variants={fadeUpVariants}
    initial={false}
    transition={{
      duration: 0.6,
      delay: 0.1,
      ease: [0.21, 0.47, 0.32, 0.98],
      type: 'spring',
    }}
  >
    Automate Your Link-Building with AI-Personalized Outreach{' '}
  </motion.h1>
)

type HeroDescriptionProps = {
  inView: boolean
}

const HeroDescription = ({ inView }: HeroDescriptionProps) => (
  <motion.p
    className="max-w-4xl text-balance text-lg text-muted-foreground tracking-tight md:text-xl"
    animate={inView ? 'animate' : 'initial'}
    variants={fadeUpVariants}
    initial={false}
    transition={{
      duration: 0.6,
      delay: 0.2,
      ease: [0.21, 0.47, 0.32, 0.98],
      type: 'spring',
    }}
  >
    Simply input your site; our AI Agent extracts relevant keywords, identifies top backlink
    prospects, sources their contact info, and crafts personalized outreach messages.{' '}
  </motion.p>
)

type HeroButtonsProps = {
  inView: boolean
}

const HeroButtons = ({ inView }: HeroButtonsProps) => (
  <motion.div
    animate={inView ? 'animate' : 'initial'}
    variants={fadeUpVariants}
    className="flex flex-col gap-4 lg:flex-row"
    initial={false}
    transition={{
      duration: 0.6,
      delay: 0.3,
      ease: [0.21, 0.47, 0.32, 0.98],
      type: 'spring',
    }}
  >
    <div className="flex flex-col gap-4 sm:flex-row ">
      <Link href={'/auth/sign-up'}>
        <Button
          size={'lg'}
          className={cn(
            'bg-black text-white shadow hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90',
            'group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden whitespace-pre rounded-md px-4 py-2 font-semibold text-base tracking-tighter focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 md:flex',
            'transform-gpu ring-offset-current transition-all duration-300 ease-out hover:ring-2 hover:ring-primary hover:ring-offset-2',
          )}
        >
          Boost My SEO Now
          <ChevronRight className="size-4 translate-x-0 transition-all duration-300 ease-out group-hover:translate-x-1" />
        </Button>
      </Link>
      <Link
        href={'/auth/sign-up'}
        className={cn(buttonVariants({ variant: 'secondary', size: 'lg' }))}
      >
        Get Started
      </Link>
    </div>
  </motion.div>
)

type HeroDashboardPreviewProps = {
  inView: boolean
}

const HeroDashboardPreview = ({ inView }: HeroDashboardPreviewProps) => (
  <motion.div
    animate={inView ? 'animate' : 'initial'}
    variants={fadeUpVariants}
    initial={false}
    transition={{
      duration: 1.4,
      delay: 0.4,
      ease: [0.21, 0.47, 0.32, 0.98],
      type: 'spring',
    }}
    className="relative mt-8 h-full w-full rounded-xl after:absolute after:inset-0 after:z-10 after:[background:linear-gradient(to_top,#fff_10%,transparent)] dark:after:[background:linear-gradient(to_top,#000000_10%,transparent)]"
  >
    <div
      className={cn(
        'absolute inset-0 bottom-1/2 h-full w-full transform-gpu [filter:blur(120px)]',
        // light styles
        '[background-image:linear-gradient(to_bottom,#ffaa40,transparent_10%)]',
        // dark styles
        'dark:[background-image:linear-gradient(to_bottom,#ffffff,transparent_10%)]',
      )}
    />

    <Image
      width={2672}
      height={1600}
      alt={siteConfig.name}
      src="/landing/dashboard-light.png"
      className="relative block h-full w-full rounded-xl border dark:hidden"
    />
    <Image
      width={3276}
      height={1766}
      alt={siteConfig.name}
      src="/landing/dashboard-dark.png"
      className="relative hidden h-full w-full rounded-xl border dark:block"
    />

    <BorderBeam size={150} colorFrom="#1a1a2e" colorTo="#16213e" />
    <BorderBeam size={150} delay={7} colorFrom="#0f3460" colorTo="#541690" reverse />
  </motion.div>
)

export function Hero() {
  const fadeInRef = useRef(null)
  const fadeInInView = useInView(fadeInRef, {
    once: true,
  })

  const avatarItems = [
    {
      imageSrc: '/pageui/people/1.webp',
      name: 'John Doe',
    },
    {
      imageSrc: '/pageui/people/2.webp',
      name: 'Jane Doe',
    },
    {
      imageSrc: '/pageui/people/3.webp',
      name: 'Alice Doe',
    },
    {
      imageSrc: '/pageui/people/4.webp',
      name: 'Bob Doe',
    },
    {
      imageSrc: '/pageui/people/5.webp',
      name: 'Eve Doe',
    },
  ]

  return (
    <section id="hero">
      <div className="relative h-full overflow-hidden py-14">
        <div className="container z-10 flex flex-col">
          <div className="mt-2 grid grid-cols-1 sm:mt-20">
            <div ref={fadeInRef} className="flex flex-col items-center gap-6 pb-8 text-center">
              <HeroTitle inView={fadeInInView} />
              <HeroDescription inView={fadeInInView} />
              <LandingSocialProof
                suffixText="get Backlinks faster"
                numberOfUsers={99}
                avatarItems={avatarItems}
              />
              <HeroButtons inView={fadeInInView} />
            </div>
          </div>

          <HeroDashboardPreview inView={fadeInInView} />
        </div>
      </div>
    </section>
  )
}
